import { useState, useEffect, useMemo, useRef, useCallback, useContext } from 'react';
import { useSearchParams } from 'react-router-dom';
import classNames from './StudentMetricsDashboard.module.scss';
import { useTranslation } from 'react-i18next';
import StudentMetrics from '@/components/school-management/StudentMetricsDashboard/StudentMetricsDashboard';
import useStudentMetricsDashboard from '@/components/school-management/StudentMetricsDashboard/useStudentMetricsDashboard';
import PageHeader from '@/components/school-management/GenericTable/PageHeader/PageHeader';
import PageTabs from '@/components/school-management/PageTabs/PageTabs';
import { useAppSelector } from '@/store';
import { authSelector } from '@/store/reducers/auth';
import UseScreenSize from '@/hooks/UseScreenSize';
import Navbar from '@/components/Navbar/Navbar';
import TeacherMyClassesStatistics from '@/pages/dashboard/my-classes/TeacherMyClassesStatistics';
import {
  FilterValue,
  FilterField,
  isEmptyValue,
} from '@/components/school-management/BimloaFilter/BimloaFilter';
import type {
  CascadingFilterRequest,
  GroupAutocompleteItem,
  TeacherAutocompleteItem,
  StudentAutocompleteItem,
} from '@/school-management/types/response/autocomplete';
import Students from '@/components/school-management/SchoolDetailsPage/Students';
import SchoolStatistics, {
  SchoolStatisticsRef,
} from '@/components/school-management/SchoolDetailsPage/SchoolStatistics';
import adminCommonUtils from '@/school-management/utils/common';
import { hasStudentTabStatsScopeFilters } from '@/school-management/utils/userStatisticsFilters';
import { EventDispatcherContext, DispatcherEvents } from '@/context/eventDispatcherContext';

// URL params and storage keys for persistence
const STUDENT_METRICS_VIEW_PARAM = 'studentsView';
const STUDENT_METRICS_DRILL_DOWN_PARAM = 'studentsDrillDown';
const STUDENT_METRICS_DRILL_DOWN_VALUES = [
  'AvgStudentGrowth',
  'AvgStudentProgressAtCurrentLevel',
  'StudentLevelProgression',
  'AvgScores',
  'StudentsAcrossSkills',
] as const;
const TEACHER_METRICS_FILTERS_STORAGE_KEY_PREFIX = 'teacher_metrics_filters_';
const TEACHER_STUDENTS_DATA_FILTERS_STORAGE_KEY_PREFIX = 'teacher_students_data_filters_';

const DEFAULT_STUDENT_FILTERS: Record<string, FilterValue> = {
  name: [],
  teacher: [],
  groupName: [],
  gradeName: [],
  startingLevel: [],
  currentLevel: [],
  status: [],
  completedActivities: {},
  score: {},
  firstLogin: { mode: 'none' },
  lastLogin: { mode: 'none' },
  displayDataFor: 'currentLevel',
};

const hasAnyFilter = (filterFields: FilterField[], filters: Record<string, FilterValue>) => {
  const anyActive = filterFields.some(f => {
    return f && filters[f.key] ? !isEmptyValue(f, filters[f.key]) : false;
  });
  return anyActive;
};

interface StudentMetricsDashboardProps {}

const StudentMetricsDashboard = ({}: StudentMetricsDashboardProps) => {
  const { t } = useTranslation();
  const { isMobile } = UseScreenSize();
  const { user } = useAppSelector(authSelector);
  const [searchParams, setSearchParams] = useSearchParams();
  const eventDispatcherContext = useContext(EventDispatcherContext);
  const organizationId = user?.metadata?.organizationDetails?.organizationId;
  const organizationName = user?.metadata?.organizationDetails?.organizationName || '';
  const isClassCodeEnabled = user?.metadata?.organizationDetails?.classCodeEnabled;

  const {
    drillDownType: studentMetricsDrillDownType,
    setDrillDownType: setStudentDashboardDrillDownType,
    filters: studentDashboardFilters,
    setFilters: setStudentDashboardFilters,
    filterFields: studentDashboardFiltersFields,
    dashboardFilter: studentDashboardApiFilter,
  } = useStudentMetricsDashboard();
  const studentDashboardToolsRef = useRef(null);
  const [showDashboard, setShowDashboard] = useState<boolean>(false);
  const metricsFiltersRestoredRef = useRef(false);
  const studentsDataFiltersRestoredRef = useRef(false);
  const [showStudentsFilters, setShowStudentsFilters] = useState<boolean>(false);

  const [studentFilters, setStudentFilters] = useState<Record<string, FilterValue>>(() => ({
    ...DEFAULT_STUDENT_FILTERS,
  }));

  const [hasSelectedStudents, setHasSelectedStudents] = useState<boolean>(false);
  const schoolStatisticsRef = useRef<SchoolStatisticsRef>(null);

  const studentFilterFields: FilterField[] = useMemo(() => {
    const allGrades = adminCommonUtils.getAllGradesForDropdown(t);
    const gradeNames = [...allGrades.map(g => g.label)];

    // Static level options (1-5)
    const startingLevels = Array.from({ length: 5 }, (_, i) => String(i + 1));
    const currentLevels = Array.from({ length: 5 }, (_, i) => String(i + 1));

    const userStatus = isClassCodeEnabled
      ? [t('active'), t('resetPassword'), t('levelTestIncomplete')]
      : [t('active'), t('inactive'), t('levelTestIncomplete')];

    const filter: FilterField[] = [
      {
        key: 'name',
        label: t('studentMetricsDashboard.filters.studentName') || 'Student Name',
        options: [],
        multi: true,
        autocompleteType: 'students',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.studentNameSearchPlaceholder') || 'Search student',
      },
      {
        key: 'teacher',
        label: t('admin.studentMetricsDashboard.filters.coTeacher') || 'Co-Teacher Name',
        options: [],
        multi: true,
        autocompleteType: 'teachers',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.teacherSearchPlaceholder') || 'Search teacher',
      },
      {
        key: 'groupName',
        label: t('studentMetricsDashboard.filters.group') || 'Class Name',
        options: [],
        multi: true,
        autocompleteType: 'groups',
        searchPlaceholder:
          t('studentMetricsDashboard.filters.groupSearchPlaceholder') || 'Search class',
      },
      {
        key: 'gradeName',
        label: t('studentMetricsDashboard.filters.grade') || 'Grade',
        options: gradeNames,
        multi: true,
        searchable: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.gradeSearchPlaceholder') || 'Search grade',
      },
      {
        key: 'startingLevel',
        label: t('studentMetricsDashboard.filters.startingLevel') || 'Starting Level',
        options: startingLevels,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.startingLevelSearchPlaceholder') || 'Search level',
      },
      {
        key: 'currentLevel',
        label: t('studentMetricsDashboard.filters.currentLevel') || 'Current Level',
        options: currentLevels,
        multi: true,
        searchPlaceholder:
          t('studentMetricsDashboard.filters.currentLevelSearchPlaceholder') || 'Search level',
      },
      {
        key: 'status',
        label: t('admin.studentMetricsDashboard.filters.status') || 'Status',
        options: userStatus,
        multi: true,
        searchPlaceholder:
          t('admin.studentMetricsDashboard.filters.statusSearchPlaceholder') || 'Search status',
      },
      {
        key: 'completedActivities',
        label: t('studentMetricsDashboard.filters.completedActivities') || 'Completed Activities',
        options: [],
        numberRangeMode: {
          min: 0,
          // No max limit - users can enter any number
        },
      },
      {
        key: 'score',
        label: t('admin.studentMetricsDashboard.filters.score') || 'Score',
        options: [],
        numberRangeMode: {
          min: 0,
          max: 100,
        },
      },
      {
        key: 'firstLogin',
        label: t('studentMetricsDashboard.filters.firstLogin') || 'First Login',
        options: ['last7', 'last30', 'specific', 'range'] as string[],
        dateMode: true,
      },
      {
        key: 'lastLogin',
        label: t('studentMetricsDashboard.filters.lastLogin') || 'Last Login',
        options: ['last7', 'last30', 'specific', 'range'] as string[],
        dateMode: true,
      },
    ];

    return filter;
  }, [t, isClassCodeEnabled]);

  // Build cascading filters so that when a class is selected, the Student Name
  // autocomplete is scoped to that class (passes groupIds to the API).

  const gradeIdMapper = useMemo(() => adminCommonUtils.getGradesLabelToIdMapper(t), [t]);

  const toTeacherIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as TeacherAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.id) : undefined;
  };

  const toGroupIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as GroupAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.groupId) : undefined;
  };

  const toStudentIds = (value: FilterValue): string[] | undefined => {
    const arr = Array.isArray(value) ? (value as StudentAutocompleteItem[]) : [];
    return arr.length > 0 ? arr.map(i => i.id) : undefined;
  };

  const toGradeIds = (value: FilterValue): number[] | undefined => {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    if (arr.length === 0) return undefined;
    const ids = arr.map(label => gradeIdMapper[label]).filter((id): id is number => id != null);
    return ids.length > 0 ? ids : undefined;
  };

  const teacherDashboardCascadingFilters = useMemo((): CascadingFilterRequest => {
    const groupArr = Array.isArray(studentDashboardFilters.group)
      ? (studentDashboardFilters.group as GroupAutocompleteItem[])
      : [];
    const groupIds = groupArr.map(g => g.groupId).filter(Boolean);
    return groupIds.length > 0 ? { groupIds } : {};
  }, [studentDashboardFilters.group]);

  const studentCascadingFilters = useMemo(
    (): CascadingFilterRequest => ({
      teacherIds: toTeacherIds(studentFilters.teacher),
      groupIds: toGroupIds(studentFilters.groupName),
      studentIds: toStudentIds(studentFilters.name),
      gradeIds: toGradeIds(studentFilters.gradeName),
    }),
    [studentFilters, gradeIdMapper]
  );

  const selectedTab =
    searchParams.get(STUDENT_METRICS_VIEW_PARAM) === 'metrics' ? 'metrics' : 'data';
  const setSelectedTab = useCallback(
    (view: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (view === 'data') next.delete(STUDENT_METRICS_VIEW_PARAM);
        else next.set(STUDENT_METRICS_VIEW_PARAM, view);
        return next;
      });
    },
    [setSearchParams]
  );

  const setStudentMetricsDrillDown = useCallback(
    (type: '' | (typeof STUDENT_METRICS_DRILL_DOWN_VALUES)[number]) => {
      setStudentDashboardDrillDownType(type);
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (type) next.set(STUDENT_METRICS_DRILL_DOWN_PARAM, type);
        else next.delete(STUDENT_METRICS_DRILL_DOWN_PARAM);
        return next;
      });
    },
    [setStudentDashboardDrillDownType, setSearchParams]
  );

  useEffect(() => {
    const urlDrill = searchParams.get(STUDENT_METRICS_DRILL_DOWN_PARAM);
    const valid = urlDrill && STUDENT_METRICS_DRILL_DOWN_VALUES.includes(urlDrill as any);
    if (valid) setStudentDashboardDrillDownType(urlDrill as any);
    else setStudentDashboardDrillDownType('');
  }, [searchParams, setStudentDashboardDrillDownType]);

  useEffect(() => {
    metricsFiltersRestoredRef.current = false;
    studentsDataFiltersRestoredRef.current = false;
  }, [organizationId]);

  useEffect(() => {
    if (selectedTab !== 'metrics' || !organizationId) return;
    const storageKey = `${TEACHER_METRICS_FILTERS_STORAGE_KEY_PREFIX}${organizationId}`;
    if (!metricsFiltersRestoredRef.current) {
      metricsFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setStudentDashboardFilters(prev => ({ ...prev, ...parsed }));
          }
        }
      } catch {
        // ignore invalid stored filters
      }
    }
  }, [selectedTab, organizationId, setStudentDashboardFilters]);

  useEffect(() => {
    if (selectedTab !== 'metrics' || !organizationId) return;
    const storageKey = `${TEACHER_METRICS_FILTERS_STORAGE_KEY_PREFIX}${organizationId}`;
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(studentDashboardFilters));
    } catch {
      // ignore quota / serialization errors
    }
  }, [selectedTab, organizationId, studentDashboardFilters]);

  useEffect(() => {
    if (selectedTab !== 'data' || !organizationId) return;
    if (!studentsDataFiltersRestoredRef.current) {
      studentsDataFiltersRestoredRef.current = true;
      try {
        const stored = sessionStorage.getItem(
          `${TEACHER_STUDENTS_DATA_FILTERS_STORAGE_KEY_PREFIX}${organizationId}`
        );
        if (stored) {
          const parsed = JSON.parse(stored) as Record<string, FilterValue>;
          if (parsed && typeof parsed === 'object') {
            setStudentFilters(prev => ({ ...DEFAULT_STUDENT_FILTERS, ...prev, ...parsed }));
          }
        }
      } catch {
        // ignore invalid stored filters
      }
    }
  }, [selectedTab, organizationId]);

  useEffect(() => {
    if (selectedTab !== 'data' || !organizationId) return;
    try {
      sessionStorage.setItem(
        `${TEACHER_STUDENTS_DATA_FILTERS_STORAGE_KEY_PREFIX}${organizationId}`,
        JSON.stringify(studentFilters)
      );
    } catch {
      // ignore quota / serialization errors
    }
  }, [selectedTab, organizationId, studentFilters]);

  const pageTabsTools = useMemo(() => {
    return <div ref={studentDashboardToolsRef} />;
  }, []);

  useEffect(() => {
    if (pageTabsTools) {
      setShowDashboard(true);
    }
  }, [pageTabsTools]);

  if (isMobile) {
    return (
      <div className={classNames.mobilePage}>
        <Navbar className={classNames.mobileNavbar}>
          <div className={classNames.mobileHeader}>
            <h2>{t('settings.my_students')}</h2>
          </div>
        </Navbar>
        <div className={classNames.mobileStatsSection}>
          <p className={classNames.mobileDesktopMessage}>{t('teacherMobile.studentsMessage')}</p>
          <TeacherMyClassesStatistics user={user} classFilters={{}} hasAnyClassFilter={false} />
        </div>
      </div>
    );
  }

  return (
    <div className={classNames.page}>
      {selectedTab === 'data' && (
        <PageHeader
          className={classNames.pageHeader}
          title={t('students')}
          subTitle={organizationName}
          filterFields={studentFilterFields}
          filters={studentFilters}
          setFilters={setStudentFilters}
          anchorFilterOverlay={true}
          anchorFilterOverlayPosition='end'
          anchorFilterOverlayHorizontalOffset={40}
          organizationId={organizationId}
          disabledFilter={!showStudentsFilters || hasSelectedStudents}
          cascadingFilters={studentCascadingFilters}
        />
      )}

      {selectedTab === 'metrics' && (
        <PageHeader
          className={classNames.pageHeader}
          title={t('students')}
          subTitle={organizationName}
          filterFields={studentDashboardFiltersFields}
          filters={studentDashboardFilters}
          setFilters={setStudentDashboardFilters}
          anchorFilterOverlay={true}
          anchorFilterOverlayPosition='end'
          anchorFilterOverlayHorizontalOffset={40}
          organizationId={organizationId}
          disabledFilter={!showStudentsFilters || hasSelectedStudents}
          cascadingFilters={teacherDashboardCascadingFilters}
          onBack={studentMetricsDrillDownType ? () => setStudentMetricsDrillDown('') : undefined}
        />
      )}

      <PageTabs
        className={classNames.pageTabs}
        tabs={['data', 'metrics'].map(tabId => ({
          id: tabId,
          label: t(`studentMetricsDashboard.pageTabs.${tabId}`),
        }))}
        selectedTab={selectedTab}
        onTabSelect={setSelectedTab}
        viewTabs={!studentMetricsDrillDownType}
        tools={pageTabsTools}
      />

      {showDashboard && selectedTab === 'data' && (
        <div className={classNames.mainContent}>
          <SchoolStatistics
            user={user}
            schoolId={organizationId}
            activeTab={'students'}
            studentFilters={studentFilters}
            hasActiveTabStatsFilters={hasStudentTabStatsScopeFilters(
              studentFilterFields,
              studentFilters
            )}
            ref={schoolStatisticsRef}
          />
          <Students
            schoolId={organizationId}
            studentFilters={studentFilters}
            setStudentFilters={setStudentFilters}
            hasAnyFilter={hasAnyFilter(studentFilterFields, studentFilters)}
            setShowStudentsStatistics={setShowStudentsFilters}
            toolsContainer={studentDashboardToolsRef}
            onStudentsSelected={(selectedStudents: any[]) => {
              setHasSelectedStudents(selectedStudents.length > 0);
            }}
            onRefreshData={() => {
              schoolStatisticsRef.current?.refetch();
              eventDispatcherContext.dispatch(DispatcherEvents.SCHOOL_DETAILS_CHANGE, null);
            }}
          />
        </div>
      )}

      {showDashboard && selectedTab === 'metrics' && (
        <StudentMetrics
          statisticsClassName={classNames.statistics}
          filters={studentDashboardApiFilter}
          drillDownType={studentMetricsDrillDownType}
          setDrillDownType={setStudentMetricsDrillDown}
          toolsContainer={studentDashboardToolsRef.current}
        />
      )}
    </div>
  );
};

export default StudentMetricsDashboard;
